import {gql} from '@apollo/client';
import {createStyles, Group, Tooltip} from '@mantine/core';
import {css} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import SchemaCollaboratorsSection from 'collaborator-components/SchemaCollaboratorsSection';
import {
  getDefaultCollaborators,
  useSchemaCollaboratorsSectionWithDefaults,
} from 'collaborator-components/useSchemaCollaboratorsSection';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {
  DefaultSchemaCollaboratorsConfigurationSchemaType,
  ProcessExecutionType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllBoxSchemasWithRegistryQuery} from 'inventory/__generated/AllBoxSchemasWithRegistryQuery';
import {AllContainerSchemasWithRegistryQuery} from 'inventory/__generated/AllContainerSchemasWithRegistryQuery';
import {AllLocationSchemasWithRegistryQuery} from 'inventory/__generated/AllLocationSchemasWithRegistryQuery';
import {AllPlateSchemasWithRegistryQuery} from 'inventory/__generated/AllPlateSchemasWithRegistryQuery';
import {
  useBoxSchemasQueryWithRegistry,
  useContainerSchemasQueryWithRegistry,
  useLocationSchemasQueryWithRegistry,
  usePlateSchemasQueryWithRegistry,
} from 'inventory/useStorableSchemasQuery';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {ProcessStatusLifecyclesQuery} from 'processes/settings/__generated/ProcessStatusLifecyclesQuery';
import {
  ProcessTaskSchemaEditorQuery,
  ProcessTaskSchemaEditorQueryVariables,
} from 'processes/settings/__generated/ProcessTaskSchemaEditorQuery';
import CloneProcessTaskSchemaModal from 'processes/settings/CloneProcessTaskSchemaModal';
import GraphBuilderFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/GraphBuilderFlowchartConfigEditor';
import {ProcessFlowchartConfigEditorHeaderActionsProps} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorHeaderTypes';
import {
  createOwnerToRegistryMap,
  getRegistryIdForOwner,
} from 'processes/settings/ProcessTaskSchemaEditorUtils';
import {insertOrReplaceTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {AllResultSchemasQuery} from 'results/__generated/AllResultSchemasQuery';
import {useAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import SettingsPageContainer, {SettingsPageContainerProps} from 'settings/SettingsPageContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';
import enumIncludes from 'util/enumIncludes';
import QueryingValidationRulesTable from 'validation_rules/components/ValidationRulesTable';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  MinimalProcessTaskSchemaEditorQuery,
  MinimalProcessTaskSchemaEditorQueryVariables,
} from './__generated/MinimalProcessTaskSchemaEditorQuery';
import {ProcessTaskSchemaEditorOrganizationsQuery} from './__generated/ProcessTaskSchemaEditorOrganizationsQuery';
import ProcessFlowchartConfigEditor from './graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditor';
import {ProcessTaskDefinitionVersionGraphQL} from './graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import OutputConfigFields from './OutputConfigFields';
import OutputConfigSection from './OutputConfigSection';
import {ProcessTaskSchemaEditorContentSections} from './ProcessTaskSchemaEditorConstants';
import ProcessTaskSchemaEditorSection from './ProcessTaskSchemaEditorSection';
import {styles} from './ProcessTaskSchemaEditorStyles';
import ProcessTaskSchemaFormsList from './ProcessTaskSchemaFormsList';
import ProcessTaskSchemaPermissionSettings from './ProcessTaskSchemaPermissionSettings';
import ProcessTaskSchemaRequestV2DefinitionsList from './ProcessTaskSchemaRequestV2DefinitionsList';
import {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import TaskConfigSection from './TaskConfigSection';
import {useProcessTaskSchemaEditorState} from './useProcessTaskSchemaEditorState';
import type {UseProcessTaskSchemaEditorStateArgs} from './useProcessTaskSchemaEditorState';
type ResultSchemasGQL = AllResultSchemasQuery['resultSchemas'][number];
type ProcessTaskStatusLifecycle = ProcessStatusLifecyclesQuery['processTaskStatusLifecycles'][number];
type PlateSchemaGQL = AllPlateSchemasWithRegistryQuery['plateSchemas'][number];
type LocationSchemaGQL = AllLocationSchemasWithRegistryQuery['locationSchemas'][number];
type ContainerSchemaGQL = AllContainerSchemasWithRegistryQuery['containerSchemas'][number];
type BoxSchemaGQL = AllBoxSchemasWithRegistryQuery['boxSchemas'][number];
type TagSchemaGQL = TagSchemasWithRegistryQuery['tagSchemas'][number];
type ProcessTaskSchemaGQL =
  AllProcessTaskSchemasForSchemaConfigQuery['processTaskSchemasSupportingPlatformFeature'][number];

const CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE =
  "Duplicating task schemas of 'Flowchart' execution type isn't supported at this time. You can duplicate task schemas of 'Direct' or 'Entry' execution type.";
export const PROCESS_TASK_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query ProcessTaskSchemaEditorQuery($schemaId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        id
        flowchartConfig {
          ...ProcessFlowchartConfigGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const STATUS_LIFECYCLES_QUERY: DocumentNode = gql`
  query ProcessStatusLifecyclesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskStatusLifecycles {
      ... on ProcessTaskStatusLifecycleNode {
        id
        name
        schemaExecutionType
      }
    }
  }
`;

export const PROCESS_TASK_SCHEMA_EDITOR_ORGANIZATIONS_QUERY: DocumentNode = gql`
  query ProcessTaskSchemaEditorOrganizationsQuery @skipAudit @engComponent(name: WORKFLOWS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query MinimalProcessTaskSchemaEditorQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        flowchartConfig {
          id
        }
        relevantForms {
          ... on FormDefinitionNode {
            id
            isValid
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    # Registries are needed to map organization IDs to registry IDs for field tables
    # this lets us link to the field's definition
    registries {
      ... on RegistryNode {
        id
        organization {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
export enum ProcessTaskSchemaTabs {
  ACCESS_POLICIES = 'access_policies',
  CONFIGURATION = 'configuration',
  FORMS = 'forms',
  VALIDATION_RULES = 'validation_rules',
  REQUEST_V2_DEFINITIONS = 'request_v2_definitions',
  SYSTEM_NAMES = 'system_names',
}

type StorableSchemaGQL = BoxSchemaGQL | ContainerSchemaGQL | LocationSchemaGQL | PlateSchemaGQL;

// exported for test only
const baseTestAttrs = {component: 'ProcessTaskSchemaEditorQuery'};
export const DATA_TEST_ATTRS = {
  action: {...baseTestAttrs, element: 'action'},
  schemaError: {...baseTestAttrs, element: 'schemaError'},
};
export function ProcessTaskSchemaHeaderActions(props: {
  processTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
}): JSX.Element {
  const isCloningDisabled = props.processTaskSchema.executionType === ProcessExecutionType.FLOWCHART;
  const [isCloningModalOpen, setIsCloningModalOpen] = useState(false);

  return (
    <Group spacing="sm">
      <Tooltip
        label={CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE}
        disabled={!isCloningDisabled}
        position="bottom"
        multiline={true}
        width={300}
      >
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={isCloningDisabled}
            onClick={() => {
              setIsCloningModalOpen(true);
            }}
            size={ButtonSize.SMALL}
            text="Duplicate"
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'cloneTaskSchemaButton'}}
          />
        </span>
      </Tooltip>
      <SchemaSettingsExportDropdown schemaId={props.processTaskSchema.id} showAuditLogExport={true} />
      {isCloningModalOpen && (
        <CloneProcessTaskSchemaModal
          originalProcessTaskSchema={props.processTaskSchema}
          onCloneSuccess={(clonedSchema) => {
            BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemaURL(clonedSchema.id));
          }}
          actionOrigin={ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE}
          actionMethod={ActionMethod.BUTTON_CLICK}
          onRequestClose={() => {
            setIsCloningModalOpen(false);
          }}
        />
      )}
    </Group>
  );
}

// Adapter to bridge generic header actions props to ProcessTaskSchemaHeaderActions
export function DefaultProcessTaskSchemaHeaderActions(
  props: ProcessFlowchartConfigEditorHeaderActionsProps
): JSX.Element {
  return <ProcessTaskSchemaHeaderActions processTaskSchema={props.rootTaskSchema} />;
}
function UnpublishedExecutionTemplateBanner(props: {onGoToTemplate: () => void}): JSX.Element {
  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text="The execution template for this task schema is not published"
      supportingInfo="Execution templates must be published so users can execute tasks created with this schema"
      action={{
        actionType: InlineBannerActionType.CUSTOM,
        actionText: 'Go to template',
        actionOnClick: props.onGoToTemplate,
      }}
    />
  );
}

export function ArchivedTaskSchemasBanner(props: {archivedNames: Array<string>}): JSX.Element {
  const names = props.archivedNames.join(', ');
  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text="This flowchart uses one or more archived task schemas"
      supportingInfo={`Users can't create this flowchart until you replace or unarchive the following task schema(s): ${names}.`}
    />
  );
}
// Load plugins for the new flowchart config editor.
// NOTE: Registration is deferred to avoid a circular dependency:
// TaskSchemaNodeAdapters -> DefaultTaskSchemaNodeDetailsSidepanelContent -> ProcessTaskSchemaEditor
//   -> DefaultProcessesPlugin -> TaskSchemaNodeAdapters
export function ensureDefaultProcessesPluginRegistered({
  ignoreFlowchartConfigFlag = false,
}: {ignoreFlowchartConfigFlag?: boolean} = {}): void {
  if (!ignoreFlowchartConfigFlag && !BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG) {
    return;
  }
  // Gate on actual registry membership, not a one-shot flag. Test teardown can deregister the
  // plugin between renders, and a stale "already registered" flag would then skip re-registering,
  // leaving the editor to render with no FLOWCHART adapter and throw.
  if (!PLUGIN_REGISTRY_INSTANCE.hasPlugin(DefaultProcessesPlugin.pluginName)) {
    new DefaultProcessesPlugin().registerForEditor();
  }
}
interface BaseProcessTaskSchemaEditorContainerProps {
  subtitle: string | null;
  processTaskSchema?: ExtractByTypename<
    MinimalProcessTaskSchemaEditorQuery['processTaskSchema'],
    'ProcessTaskSchemaNode'
  >;
}

interface ProcessTaskSchemaEditorContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  children: React.ReactNode;
}

interface ProcessTaskSchemaEditorTabbedContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  schemaId: string;
  configurationTab: React.ReactNode;
  systemNamesTab?: React.ReactNode;
  tabContentClassName?: string;
  tabNavClassName?: string;
  tabPaneClassName?: string;
}

function handleNavigateToSchemaListing(): void {
  BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemasURL());
}

function makeProcessTaskSchemaSettingsPageContainerProps(
  props: BaseProcessTaskSchemaEditorContainerProps
): SettingsPageContainerProps {
  return {
    onClickTitle: handleNavigateToSchemaListing,
    subtitle: `${props.subtitle ?? 'Create Task Schema'}`,
    title: 'Task Schemas',
    toolbar: props.processTaskSchema && licenses.hasEnterprisePlan() && (
      <ProcessTaskSchemaHeaderActions processTaskSchema={props.processTaskSchema} />
    ),
  };
}

function ProcessTaskSchemaEditorContainer(props: ProcessTaskSchemaEditorContainerProps): JSX.Element {
  return (
    <SettingsPageContainer {...makeProcessTaskSchemaSettingsPageContainerProps(props)}>
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section">{props.children}</div>
      </div>
    </SettingsPageContainer>
  );
}

export function ProcessTaskSchemaEditorTabbedContainer(
  props: ProcessTaskSchemaEditorTabbedContainerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<ProcessTaskSchemaTabs>(() => {
    const hashedTab = BenchlingUtil.getLocationHash();
    return enumIncludes(ProcessTaskSchemaTabs, hashedTab) ? hashedTab : ProcessTaskSchemaTabs.CONFIGURATION;
  });

  const formsEnabled = isWorkflowCreateTaskFormsEnabled();
  const validationRulesEnabled = BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS;
  const requestsV2Enabled =
    BenchlingConfig.REQUESTS_V2_ENABLE && isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER});
  const accessPoliciesEnabled = BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI;
  const hasSystemNamesTab = props.systemNamesTab != null;

  const availableTabs = useMemo(() => {
    const tabs = new Set<ProcessTaskSchemaTabs>([ProcessTaskSchemaTabs.CONFIGURATION]);
    if (formsEnabled) {
      tabs.add(ProcessTaskSchemaTabs.FORMS);
    }
    if (validationRulesEnabled) {
      tabs.add(ProcessTaskSchemaTabs.VALIDATION_RULES);
    }
    if (requestsV2Enabled) {
      tabs.add(ProcessTaskSchemaTabs.REQUEST_V2_DEFINITIONS);
    }
    if (hasSystemNamesTab) {
      tabs.add(ProcessTaskSchemaTabs.SYSTEM_NAMES);
    }
    if (accessPoliciesEnabled) {
      tabs.add(ProcessTaskSchemaTabs.ACCESS_POLICIES);
    }
    return tabs;
  }, [formsEnabled, validationRulesEnabled, requestsV2Enabled, hasSystemNamesTab, accessPoliciesEnabled]);

  const resolvedActiveTab = availableTabs.has(activeTab) ? activeTab : ProcessTaskSchemaTabs.CONFIGURATION;

  useEffect(() => {
    if (resolvedActiveTab !== activeTab) {
      setActiveTab(resolvedActiveTab);
      BenchlingUtil.clearLocationHash();
    }
  }, [resolvedActiveTab, activeTab]);

  const onChangeTab = (tab: ProcessTaskSchemaTabs): void => {
    setActiveTab(tab);
    if (tab === ProcessTaskSchemaTabs.CONFIGURATION) {
      BenchlingUtil.clearLocationHash();
    } else {
      BenchlingUtil.setLocationHash(tab);
    }
  };

  const sectionClassName = classNames('settingsPageContainer-section', props.tabContentClassName);

  return (
    <TabContainer<ProcessTaskSchemaTabs>
      activeTab={resolvedActiveTab}
      onRequestTabChange={onChangeTab}
      className="u-flex-grow u-min-width-zero"
      navClassName={props.tabNavClassName}
      paneClassName={props.tabPaneClassName}
    >
      <Tab tabKey={ProcessTaskSchemaTabs.CONFIGURATION} title="Configuration">
        <div className={sectionClassName}>{props.configurationTab}</div>
      </Tab>
      {formsEnabled && (
        <Tab tabKey={ProcessTaskSchemaTabs.FORMS} title="Forms">
          <div className={sectionClassName}>
            <ProcessTaskSchemaFormsList
              schemaId={props.schemaId}
              processTaskSchema={props.processTaskSchema!}
            />
          </div>
        </Tab>
      )}
      {validationRulesEnabled && (
        <Tab tabKey={ProcessTaskSchemaTabs.VALIDATION_RULES} title="Validation Rules">
          <div className={sectionClassName}>
            <QueryingValidationRulesTable
              hasValidationRulesItemId={props.schemaId}
              onSubmit={ProcessSchemaDAO.updateValidationRulesForProcessTaskSchema}
            />
          </div>
        </Tab>
      )}
      {requestsV2Enabled && (
        <Tab tabKey={ProcessTaskSchemaTabs.REQUEST_V2_DEFINITIONS} title="Request Templates">
          <div className={sectionClassName}>
            <ProcessTaskSchemaRequestV2DefinitionsList taskSchemaId={props.schemaId} />
          </div>
        </Tab>
      )}
      {hasSystemNamesTab && (
        <Tab tabKey={ProcessTaskSchemaTabs.SYSTEM_NAMES} title="System Names">
          <div className={sectionClassName}>{props.systemNamesTab}</div>
        </Tab>
      )}
      {accessPoliciesEnabled && (
        <Tab tabKey={ProcessTaskSchemaTabs.ACCESS_POLICIES} title="Access Policies">
          <div className={sectionClassName}>
            <ProcessTaskSchemaPermissionSettings processTaskSchemaId={props.schemaId} />
          </div>
        </Tab>
      )}
    </TabContainer>
  );
}
interface ProcessTaskSchemaEditorContentProps {
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  tagSchemas: Array<TagSchemaGQL> | null;
  taskSchemas: Array<ProcessTaskSchemaGQL> | null;
  statusLifecycles: Array<ProcessTaskStatusLifecycle> | null;
  refetchProcessSchema?: () => void;
  beforeTaskSchemaUpdate?: () => void;
  afterTaskSchemaUpdate?: (
    newTaskSchema: ProcessTaskSchemaGraphQL,
    newTaskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL | null
  ) => void;
  resultSchemas: Array<ResultSchemasGQL> | null;
  isAnyPartOfFlowchartConfigInaccessible: boolean | null;
  sectionsToHide?: Array<ProcessTaskSchemaEditorContentSections>;
  locked?: boolean;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest) => Promise<void>;
  noSectionSpacing?: boolean;
  taskDefinitionId?: string;
  isSettingsPage?: boolean;
  registryId?: string;
  stickyCreateButton?: boolean;
  // The five props below (`handleNext`, `collaboratorsToApplyAfterCreate`,
  // `registerSubmitHandler`, `onOwnerChange`, and the related `handleCreateProcessTaskSchema`
  // plumbing in `useProcessTaskSchemaEditorState`) exist to bridge this editor to the multi-step
  // access policies create flow wrapper while the legacy single-step create UI still exists
  // (gated on `PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI` being off). Once that flag
  // is consolidated and the multi-step flow is the only create path, these props can be deleted
  // and the wrapper restructured to own the editor state directly (matching the studies /
  // entities pattern in `SchemaWithOrgPermissionsCreateFlow`), at which point the `display:
  // none` trick used to keep the editor mounted across step transitions also goes away.

  /**
   * When provided, the inline "Create" button is rendered as a "Next" button that invokes this
   * callback after the local validation gate passes. Used by the multi-step access policies
   * create flow to advance to the Access Policies step instead of immediately submitting.
   */
  handleNext?: () => void;
  /**
   * Collaborators to apply to the newly-created task schema. Only used when `handleNext` is
   * absent (i.e., the create button is the actual submit). Threaded into the editor state hook
   * so that the default create path applies these collaborators before navigation.
   */
  collaboratorsToApplyAfterCreate?: Array<SchemaCollaborator> | null;
  /**
   * When provided, the editor invokes this with a reference to its `handleCreateProcessTaskSchema`
   * action whenever it changes. Used by the multi-step access policies create flow so the
   * wrapper's Step 2 "Create" button can submit the schema from inside the editor state.
   */
  registerSubmitHandler?: (handler: (() => Promise<void>) | null) => void;
  /**
   * When provided, the editor invokes this with the current owner organization id whenever it
   * changes. Used by the multi-step access policies create flow to re-seed collaborator
   * defaults from the newly-selected owner's `OrganizationDefaultSchemaCollaboratorsConfiguration`.
   */
  onOwnerChange?: (ownerId: string | null) => void;
}

export function ProcessTaskSchemaEditorContent(props: ProcessTaskSchemaEditorContentProps): JSX.Element {
  const stateArgs: UseProcessTaskSchemaEditorStateArgs = {
    processTaskSchema: props.processTaskSchema,
    storableSchemas: props.storableSchemas,
    tagSchemas: props.tagSchemas,
    taskSchemas: props.taskSchemas,
    statusLifecycles: props.statusLifecycles,
    refetchProcessSchema: props.refetchProcessSchema,
    beforeTaskSchemaUpdate: props.beforeTaskSchemaUpdate,
    afterTaskSchemaUpdate: props.afterTaskSchemaUpdate,
    resultSchemas: props.resultSchemas,
    isAnyPartOfFlowchartConfigInaccessible: props.isAnyPartOfFlowchartConfigInaccessible,
    sectionsToHide: props.sectionsToHide,
    locked: props.locked,
    restrictedToExecutionType: props.restrictedToExecutionType,
    restrictedToOrganizationId: props.restrictedToOrganizationId,
    createProcessTaskSchema: props.createProcessTaskSchema,
    noSectionSpacing: props.noSectionSpacing,
    taskDefinitionId: props.taskDefinitionId,
    isSettingsPage: props.isSettingsPage,
    registryId: props.registryId,
    stickyCreateButton: props.stickyCreateButton,
    collaboratorsToApplyAfterCreate: props.collaboratorsToApplyAfterCreate,
  };
  const state = useProcessTaskSchemaEditorState(stateArgs);

  // Allow a parent (e.g. the multi-step create flow wrapper) to imperatively submit the schema.
  // We re-register on every render whose handler identity changed so the parent always has the
  // most up-to-date closure (which captures the latest collaborators, fields, etc.).
  const {registerSubmitHandler} = props;
  useEffect(() => {
    if (registerSubmitHandler == null) {
      return undefined;
    }
    registerSubmitHandler(state.handleCreateProcessTaskSchema);
    return () => {
      registerSubmitHandler(null);
    };
  }, [registerSubmitHandler, state.handleCreateProcessTaskSchema]);

  // Notify the parent when the selected owner changes so it can re-seed collaborator defaults
  // for the multi-step access policies create flow.
  const {onOwnerChange} = props;
  const currentOwnerId = state.schemaOwner?.id ?? null;
  useEffect(() => {
    onOwnerChange?.(currentOwnerId);
  }, [onOwnerChange, currentOwnerId]);

  if (state.schemaOwner == null) {
    return <NoOwnersSchemaMessage pluralName="workflow task schemas" />;
  }

  return (
    <>
      {state.shouldShowTemplateUnpublishedBanner && (
        <div className={css(styles.configurationWarningBanner)}>
          <UnpublishedExecutionTemplateBanner
            onGoToTemplate={() => {
              BenchlingUtil.navigate(null, props.processTaskSchema!.entryTemplate!.editUrl);
            }}
          />
        </div>
      )}
      {state.shouldShowTemplateArchivedBanner && (
        <div className={css(styles.configurationWarningBanner)}>
          <ArchivedTaskSchemasBanner
            archivedNames={state.flowchartArchivedTaskSchemas
              .map((taskSchema) =>
                isInaccessibleInfo(taskSchema) ? '(Inaccessible)' : (taskSchema as {name: string}).name
              )
              .filter((n) => n.length > 0)}
          />
        </div>
      )}
      {!state.shouldHideSection(ProcessTaskSchemaEditorContentSections.TASK) && (
        <TaskConfigSection
          state={state}
          processTaskSchema={props.processTaskSchema}
          storableSchemas={props.storableSchemas}
          resultSchemas={props.resultSchemas}
          registryId={props.registryId}
          noSectionSpacing={props.noSectionSpacing}
          isSettingsPage={props.isSettingsPage}
          restrictedToOrganizationId={props.restrictedToOrganizationId}
          restrictedToExecutionType={props.restrictedToExecutionType}
        />
      )}
      {/* When the Output section is hidden but the task schema has an output, render the OutputConfigFields. */}
      {!state.shouldHideSection(ProcessTaskSchemaEditorContentSections.TASK) &&
        state.shouldHideSection(ProcessTaskSchemaEditorContentSections.OUTPUT) &&
        state.hasOutputSchema && (
          <OutputConfigFields state={state} processTaskSchema={props.processTaskSchema} />
        )}
      {!state.shouldHideSection(ProcessTaskSchemaEditorContentSections.OUTPUT) && (
        <OutputConfigSection
          state={state}
          processTaskSchema={props.processTaskSchema}
          storableSchemas={props.storableSchemas}
          resultSchemas={props.resultSchemas}
          registryId={props.registryId}
          isSettingsPage={props.isSettingsPage}
        />
      )}
      {state.canConfigureFlowchart &&
        !BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG &&
        !state.shouldHideSection(ProcessTaskSchemaEditorContentSections.FLOWCHART) && (
          <ProcessTaskSchemaEditorSection
            isLastSection={true}
            sectionTitle="Flowchart"
            sectionNumber={3}
            shouldHideSectionHeader={state.shouldHideSectionHeaders}
          >
            {state.isBeingCreated ? (
              <span className={css(styles.descriptionText, styles.flowchartText)}>
                Configure the flowchart after the task schema is created.
              </span>
            ) : props.isAnyPartOfFlowchartConfigInaccessible ? (
              <ErrorList errorMessage="You do not have access to all of the flowchart data.  Please contact your administrator for assistance." />
            ) : (
              <>
                <ErrorList errorMessage={state.errors.flowchartConfig} />
                <GraphBuilderFlowchartConfigEditor
                  key={state.flowchartConfigTableKey}
                  rootTaskSchema={props.processTaskSchema!}
                  flowchartConfig={
                    props.processTaskSchema!.flowchartConfig?.currentFlowchartConfigVersion ?? null
                  }
                  allSchemasById={state.allSchemasById}
                  allTaskSchemas={props.taskSchemas}
                  isEditing={state.isEditingFlowchart}
                  onSetIsEditing={state.setIsEditingFlowchart}
                  onSubmit={(
                    flowchartConfig,
                    configEditorEventGroupId,
                    didInitialFlowchartHaveChainedRouters
                  ) => {
                    state.updateProcessTaskSchemaWithFields({
                      flowchartConfig,
                      configEditorEventGroupId,
                      didInitialFlowchartHaveChainedRouters,
                    });
                  }}
                />
              </>
            )}
          </ProcessTaskSchemaEditorSection>
        )}
      {props.processTaskSchema == null &&
        (() => {
          // When the multi-step access policies flow wraps this editor, swap the inline submit
          // button for a Next button — the actual submit happens from the wrapper's Access
          // Policies step.
          const isNextMode = props.handleNext != null;
          const createButton = (
            <Button
              className={!props.stickyCreateButton ? css(styles.createButton) : undefined}
              buttonStyle={ButtonStyle.SECONDARY}
              disabled={state.validationErrorMessage != null}
              onClick={isNextMode ? props.handleNext : state.handleCreateProcessTaskSchema}
              size={ButtonSize.MEDIUM}
              text={isNextMode ? 'Next' : 'Create'}
              dataTestAttrs={DATA_TEST_ATTRS.action}
            />
          );
          return props.stickyCreateButton ? (
            <div className={css(styles.stickyCreateButtonFooter)}>
              {state.validationErrorMessage ? (
                <div
                  className={css(styles.stickyCreateButtonFooterMessage)}
                  {...getTestProps(DATA_TEST_ATTRS.schemaError)}
                >
                  <InlineBanner bannerType={InlineBannerType.WARNING} text={state.validationErrorMessage} />
                </div>
              ) : (
                <span
                  className={css(styles.stickyCreateButtonFooterMessage)}
                  {...getTestProps({
                    component: 'ProcessTaskSchemaEditor',
                    element: 'stickyCreateButtonFooterMessage',
                  })}
                >
                  Create this task schema to save your edits.
                </span>
              )}
              {createButton}
            </div>
          ) : (
            <>
              {createButton}
              {state.validationErrorMessage && (
                <div
                  className={css(styles.schemaErrorMessage)}
                  {...getTestProps(DATA_TEST_ATTRS.schemaError)}
                >
                  <InlineBanner bannerType={InlineBannerType.WARNING} text={state.validationErrorMessage} />
                </div>
              )}
            </>
          );
        })()}
    </>
  );
}
interface InnerProcessTaskSchemaEditorPropsDataLoading {
  schemaId: string | null;
  storableSchemas: null;
  tagSchemas: null;
  taskSchemas: null;
  resultSchemas: null;
  statusLifecycles: null;
  isAnyPartOfFlowchartConfigInaccessible: null;
  registryId?: string;
}

interface InnerProcessTaskSchemaEditorPropsWithData {
  schemaId: string | null;
  storableSchemas: Array<StorableSchemaGQL>;
  tagSchemas: Array<TagSchemaGQL>;
  taskSchemas: Array<ProcessTaskSchemaGQL>;
  resultSchemas: Array<ResultSchemasGQL>;
  statusLifecycles: Array<ProcessTaskStatusLifecycle>;
  isAnyPartOfFlowchartConfigInaccessible: boolean;
  registryId?: string;
}

type InnerProcessTaskSchemaEditorProps =
  | InnerProcessTaskSchemaEditorPropsDataLoading
  | InnerProcessTaskSchemaEditorPropsWithData;

interface ProcessTaskSchemaEditorForCreateProps {
  schemaId: null;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (
    processTaskSchemaSpec: ProcessSchemaDAO.CreateProcessTaskSchemaRequest
  ) => Promise<void>;
  isSettingsPage?: boolean;
  stickyCreateButton?: boolean;
}

interface ProcessTaskSchemaEditorForEditProps {
  schemaId: string;
}

export type ProcessTaskSchemaEditorProps =
  | ProcessTaskSchemaEditorForCreateProps
  | ProcessTaskSchemaEditorForEditProps;

function isCreatingNewTaskSchema(
  props: ProcessTaskSchemaEditorProps
): props is ProcessTaskSchemaEditorForCreateProps {
  return props.schemaId == null;
}

function isEditingExistingTaskSchema(
  props: ProcessTaskSchemaEditorProps
): props is ProcessTaskSchemaEditorForEditProps {
  return props.schemaId != null;
}

const LoadingProcessTaskSchemaEditor = (): JSX.Element => (
  <ProcessTaskSchemaEditorContainer subtitle="">
    <HorizontalThrobber />
  </ProcessTaskSchemaEditorContainer>
);

function InnerProcessTaskSchemaEditor(props: {
  schemaId: string | null;
  processTaskSchema: ProcessTaskSchema | null;
  refetchProcessSchema?: () => void;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest) => Promise<void>;
  isSettingsPage?: boolean;
  registryId?: string;
  stickyCreateButton?: boolean;
  handleNext?: () => void;
  collaboratorsToApplyAfterCreate?: Array<SchemaCollaborator> | null;
  registerSubmitHandler?: (handler: (() => Promise<void>) | null) => void;
  onOwnerChange?: (ownerId: string | null) => void;
}): JSX.Element {
  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  const boxSchemasResult = useBoxSchemasQueryWithRegistry();
  const containerSchemasResult = useContainerSchemasQueryWithRegistry();
  const locationSchemasResult = useLocationSchemasQueryWithRegistry();
  const plateSchemasResult = usePlateSchemasQueryWithRegistry();
  const resultSchemasResult = useAllResultSchemasQuery();
  const processTaskSchemasResult = useBenchlingQuery<AllProcessTaskSchemasForSchemaConfigQuery>(
    ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY
  );
  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);

  const flowchartConfigVersion = props.processTaskSchema?.flowchartConfig?.currentFlowchartConfigVersion;

  const isAnyPartOfFlowchartConfigInaccessible: boolean =
    flowchartConfigVersion != null &&
    !isInaccessible(flowchartConfigVersion) &&
    flowchartConfigVersion.availableSchemas.some((schema) => isInaccessible(schema));

  let innerProps: InnerProcessTaskSchemaEditorProps;

  if (
    tagSchemasResult.type === QueryResultType.SUCCESS &&
    boxSchemasResult.type === QueryResultType.SUCCESS &&
    containerSchemasResult.type === QueryResultType.SUCCESS &&
    locationSchemasResult.type === QueryResultType.SUCCESS &&
    plateSchemasResult.type === QueryResultType.SUCCESS &&
    resultSchemasResult.type === QueryResultType.SUCCESS &&
    processTaskSchemasResult.type === QueryResultType.SUCCESS &&
    statusLifecyclesResult.type === QueryResultType.SUCCESS
  ) {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: [
        ...boxSchemasResult.data.boxSchemas,
        ...containerSchemasResult.data.containerSchemas,
        ...locationSchemasResult.data.locationSchemas,
        ...plateSchemasResult.data.plateSchemas,
      ],
      tagSchemas: tagSchemasResult.data.tagSchemas,
      taskSchemas:
        props.processTaskSchema != null
          ? insertOrReplaceTaskSchema(
              processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
              props.processTaskSchema
            )
          : processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
      resultSchemas: resultSchemasResult.data.resultSchemas,
      statusLifecycles: statusLifecyclesResult.data.processTaskStatusLifecycles,
      isAnyPartOfFlowchartConfigInaccessible,
      registryId: props.registryId,
    };
  } else {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: null,
      tagSchemas: null,
      taskSchemas: null,
      resultSchemas: null,
      statusLifecycles: null,
      isAnyPartOfFlowchartConfigInaccessible: null,
      registryId: props.registryId,
    };
  }

  const isSchemaNotWritable =
    props.processTaskSchema != null && !props.processTaskSchema.permissions.writable;

  return (
    <ProcessTaskSchemaEditorContent
      processTaskSchema={props.processTaskSchema}
      refetchProcessSchema={props.refetchProcessSchema}
      restrictedToExecutionType={props.restrictedToExecutionType}
      restrictedToOrganizationId={props.restrictedToOrganizationId}
      createProcessTaskSchema={props.createProcessTaskSchema}
      isSettingsPage={props.isSettingsPage}
      stickyCreateButton={props.stickyCreateButton}
      handleNext={props.handleNext}
      collaboratorsToApplyAfterCreate={props.collaboratorsToApplyAfterCreate}
      registerSubmitHandler={props.registerSubmitHandler}
      onOwnerChange={props.onOwnerChange}
      {...innerProps}
      locked={isSchemaNotWritable}
    />
  );
}

export function NonConfigurableSchema(): JSX.Element {
  return <div>This task schema is not user-configurable.</div>;
}

function shouldRenderTabbedContainer(): boolean {
  return (
    isWorkflowCreateTaskFormsEnabled() ||
    BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS ||
    BenchlingConfig.REQUESTS_V2_ENABLE ||
    BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI
  );
}

enum ProcessTaskSchemaCreateStepKey {
  METADATA = 'METADATA',
  ACCESS_POLICIES = 'ACCESS_POLICIES',
}

/**
 * Multi-step wrapper around the task schema editor that adds an "Access Policies" step before
 * submission, mirroring the pattern used by study and entity schema creation. The editor for the
 * metadata step is always mounted (visually hidden on the Access Policies step via `display:
 * none`) so that the in-progress schema state survives navigation between steps.
 */
const useMultiStepCreateFlowStyles = createStyles((theme) => ({
  stepNavContainer: {
    marginBottom: theme.spacing.xxl,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.md,
    marginTop: theme.spacing.xxl,
  },
}));

function ProcessTaskSchemaCreateFlowWithAccessPolicies(props: {
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  organizations: Array<OrganizationDefaultSchemaCollaboratorsConfiguration>;
}): JSX.Element {
  const {classes: multiStepStyles} = useMultiStepCreateFlowStyles();
  const [currentStepKey, setCurrentStepKey] = useState(ProcessTaskSchemaCreateStepKey.METADATA);
  const submitHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const registerSubmitHandler = useCallback((handler: (() => Promise<void>) | null) => {
    submitHandlerRef.current = handler;
  }, []);

  // Track the owner the editor has currently selected so we can seed collaborator defaults from
  // the matching `OrganizationDefaultSchemaCollaboratorsConfiguration`. The editor reports owner
  // changes via `onOwnerChange`; before the first report we use the restriction (if any) or the
  // first available org as the initial value, matching the editor's own initial selection.
  const initialOwnerId = props.restrictedToOrganizationId ?? props.organizations[0]?.id ?? null;
  const [currentOwnerId, setCurrentOwnerId] = useState<string | null>(initialOwnerId);

  const selectedOrganization =
    props.organizations.find((org) => org.id === currentOwnerId) ?? props.organizations[0];
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSectionWithDefaults(
    selectedOrganization,
    DefaultSchemaCollaboratorsConfigurationSchemaType.PROCESS_TASK
  );
  // Re-seed defaults whenever the owner organization changes — the user may have switched orgs
  // in the metadata step after the collaborators state was first initialized.
  useEffect(() => {
    handleUpdate(
      getDefaultCollaborators(
        selectedOrganization,
        DefaultSchemaCollaboratorsConfigurationSchemaType.PROCESS_TASK
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization.id]);

  const steps = [
    {key: ProcessTaskSchemaCreateStepKey.METADATA, title: 'Task Schema Settings'},
    {key: ProcessTaskSchemaCreateStepKey.ACCESS_POLICIES, title: 'Access Policies'},
  ];
  const isMetadataStep = currentStepKey === ProcessTaskSchemaCreateStepKey.METADATA;

  return (
    <>
      <div className={multiStepStyles.stepNavContainer}>
        <StepNav<ProcessTaskSchemaCreateStepKey>
          steps={steps}
          statusByStep={{
            [ProcessTaskSchemaCreateStepKey.METADATA]: StepItemStatus.UNVISITED,
            [ProcessTaskSchemaCreateStepKey.ACCESS_POLICIES]: StepItemStatus.UNVISITED,
          }}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={setCurrentStepKey}
        />
      </div>
      <div style={{display: isMetadataStep ? 'block' : 'none'}}>
        <InnerProcessTaskSchemaEditor
          schemaId={null}
          processTaskSchema={null}
          restrictedToExecutionType={props.restrictedToExecutionType}
          restrictedToOrganizationId={props.restrictedToOrganizationId}
          isSettingsPage={true}
          registryId={undefined}
          handleNext={() => {
            setCurrentStepKey(ProcessTaskSchemaCreateStepKey.ACCESS_POLICIES);
          }}
          collaboratorsToApplyAfterCreate={minimalCollaborators}
          registerSubmitHandler={registerSubmitHandler}
          onOwnerChange={setCurrentOwnerId}
        />
      </div>
      {!isMetadataStep && (
        <>
          <SchemaCollaboratorsSection
            isReadOnly={false}
            collaborators={collaborators}
            onUpdate={handleUpdate}
          />
          <div className={multiStepStyles.footer}>
            <Button
              buttonStyle={ButtonStyle.LINK}
              text="Back"
              onClick={() => {
                setCurrentStepKey(ProcessTaskSchemaCreateStepKey.METADATA);
              }}
              dataTestAttrs={{component: 'ProcessTaskSchemaCreateFlow', element: 'backButton'}}
            />
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Create"
              onClick={() => {
                // The inline Create button on the (hidden) metadata step has already been
                // re-purposed into "Next"; we trigger submission here via the handler the
                // editor registered through `registerSubmitHandler`. The editor state knows
                // about the collaborators selected on this step via
                // `collaboratorsToApplyAfterCreate`.
                submitHandlerRef.current?.();
              }}
              dataTestAttrs={{component: 'ProcessTaskSchemaCreateFlow', element: 'createButton'}}
            />
          </div>
        </>
      )}
    </>
  );
}

function MultiStepCreateFlowLoader(props: {
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
}): JSX.Element {
  const organizationsResult = useBenchlingQuery<ProcessTaskSchemaEditorOrganizationsQuery>(
    PROCESS_TASK_SCHEMA_EDITOR_ORGANIZATIONS_QUERY
  );
  if (organizationsResult.type !== QueryResultType.SUCCESS) {
    return <HorizontalThrobber />;
  }

  const organizations = organizationsResult.data.currentTenantOrganizations;
  if (organizations.length === 0) {
    return <NoOwnersSchemaMessage pluralName="workflow task schemas" />;
  }
  return (
    <ProcessTaskSchemaCreateFlowWithAccessPolicies
      restrictedToExecutionType={props.restrictedToExecutionType}
      restrictedToOrganizationId={props.restrictedToOrganizationId}
      organizations={organizations}
    />
  );
}

export default function ProcessTaskSchemaEditor(props: ProcessTaskSchemaEditorProps): JSX.Element {
  // We intentionally close the sidepanel when opening a task schema editor
  useOnMount(() => {
    if (
      (isCreatingNewTaskSchema(props) && Boolean(props.isSettingsPage ?? true)) ||
      isEditingExistingTaskSchema(props)
    ) {
      SidepanelActions.collapse();
    }
  });

  if (props.schemaId == null) {
    const isSettingsPage = props.isSettingsPage ?? true;
    const useMultiStepFlow =
      isSettingsPage && BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI;
    const innerEditor = useMultiStepFlow ? (
      <MultiStepCreateFlowLoader
        restrictedToExecutionType={props.restrictedToExecutionType}
        restrictedToOrganizationId={props.restrictedToOrganizationId}
      />
    ) : (
      <InnerProcessTaskSchemaEditor
        schemaId={null}
        processTaskSchema={null}
        restrictedToExecutionType={props.restrictedToExecutionType}
        restrictedToOrganizationId={props.restrictedToOrganizationId}
        createProcessTaskSchema={props.createProcessTaskSchema}
        isSettingsPage={props.isSettingsPage}
        stickyCreateButton={props.stickyCreateButton}
        registryId={undefined}
      />
    );
    if (!isSettingsPage) {
      return innerEditor;
    }
    return <ProcessTaskSchemaEditorContainer subtitle={null}>{innerEditor}</ProcessTaskSchemaEditorContainer>;
  }

  return (
    <BenchlingQuery<MinimalProcessTaskSchemaEditorQuery, MinimalProcessTaskSchemaEditorQueryVariables>
      query={MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY}
      renderLoading={LoadingProcessTaskSchemaEditor}
      variables={{schemaId: props.schemaId}}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) =>
        (() => {
          if (isInaccessible(data.processTaskSchema)) {
            return (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={data.processTaskSchema}
                modelType="processTaskSchema"
              />
            );
          }

          if (!canConfigureProcessTaskSchema(data.processTaskSchema)) {
            return <NonConfigurableSchema />;
          }

          const ownerToRegistryMap = createOwnerToRegistryMap(data.registries);
          const schemaOwner = data.processTaskSchema.organization;
          const registryId = getRegistryIdForOwner(schemaOwner.id, ownerToRegistryMap);

          const showFlowchartOnlyEditor =
            BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG &&
            isFlowchartType(data.processTaskSchema.executionType) &&
            data.processTaskSchema.flowchartConfig != null;

          if (showFlowchartOnlyEditor) {
            ensureDefaultProcessesPluginRegistered();
          }
          // In the flowchart first config paradigm, the flowchart config editor is the main entrypoint for
          // editing flowcharts.
          // Users can edit fields in the root task or output schema by interacting with the root and output
          // nodes directly.
          // TODO(BNCH-133942): Migrate inaccessible and archived node schemas in the new editor.
          const innerEditor = showFlowchartOnlyEditor ? (
            <ProcessFlowchartConfigEditor
              flowchartConfigId={data.processTaskSchema.flowchartConfig!.id}
              flowchartTypeName={DefaultProcessesPlugin.FLOWCHART_TYPE_NAME}
              locked={!data.processTaskSchema.permissions.writable}
              ownerToRegistryMap={ownerToRegistryMap}
            />
          ) : (
            // Using the pre-flowchart-first-config fragments when querying for the flowchart config results
            // in backend crashes when loading schemas which rely on task definition fields. So here we
            // explicitly query for the flowchart config using the old fragments only if we're using the old
            // editor.
            <BenchlingQuery<ProcessTaskSchemaEditorQuery, ProcessTaskSchemaEditorQueryVariables>
              query={PROCESS_TASK_SCHEMA_EDITOR_QUERY}
              renderLoading={LoadingProcessTaskSchemaEditor}
              variables={{schemaId: props.schemaId}}
              skip={data.processTaskSchema.flowchartConfig == null}
              fetchPolicy="cache-and-network"
            >
              {(innerData, result) => {
                const fullTaskSchema = {
                  ...assertIsAccessible(data.processTaskSchema),
                  flowchartConfig:
                    result.type === QueryResultType.SUCCESS
                      ? assertIsAccessible(innerData.processTaskSchema).flowchartConfig
                      : null,
                };
                return (
                  <InnerProcessTaskSchemaEditor
                    schemaId={props.schemaId}
                    processTaskSchema={fullTaskSchema}
                    refetchProcessSchema={refetch}
                    registryId={registryId}
                  />
                );
              }}
            </BenchlingQuery>
          );

          if (showFlowchartOnlyEditor) {
            // The flowchart-only editor already renders its own container.
            // Do not wrap it in SettingsPageContainer.
            return innerEditor;
          }

          if (shouldRenderTabbedContainer()) {
            return (
              <SettingsPageContainer
                {...makeProcessTaskSchemaSettingsPageContainerProps({
                  subtitle: data.processTaskSchema.name,
                  processTaskSchema: data.processTaskSchema,
                })}
              >
                <ProcessTaskSchemaEditorTabbedContainer
                  subtitle={data.processTaskSchema.name}
                  processTaskSchema={data.processTaskSchema}
                  configurationTab={innerEditor}
                  schemaId={props.schemaId}
                />
              </SettingsPageContainer>
            );
          }
          return (
            <ProcessTaskSchemaEditorContainer
              subtitle={data.processTaskSchema.name}
              processTaskSchema={data.processTaskSchema}
            >
              {innerEditor}
            </ProcessTaskSchemaEditorContainer>
          );
        })()
      }
    </BenchlingQuery>
  );
}
